import {
  useEffect,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  BarChart3,
  BookOpenText,
  Bot,
  ChartNoAxesCombined,
  CircleCheck,
  CircleHelp,
  Clock3,
  ExternalLink,
  FileQuestion,
  Gauge,
  Link2,
  Menu,
  MousePointerClick,
  Search,
  Settings,
  Swords,
  Trophy,
  type LucideIcon,
} from "lucide-react";
import { SeoPage } from "./SeoPage";
import {
  BenchmarksPage,
  CompanyBenchmarkPage,
  IndustryBenchmarkPage,
  benchmarkCompanySlug,
  benchmarkIndustries,
} from "./BenchmarkPages";

const repoUrl = "https://github.com/aeokit-dev/aeokit";
const agentUrl = "https://www.npmjs.com/package/aeo-agent";
const skillsUrl = "https://github.com/aeokit-dev/aeokit-skills";
const cloudUrl = "https://cloud.aeokit.dev";
const quickStart =
  "git clone https://github.com/aeokit-dev/aeokit.git && cd aeokit";

const providers = [
  {
    name: "OpenAI",
    surface: "Responses API",
    retrieval: "Native web search",
    credential: "OPENAI_API_KEY",
  },
  {
    name: "Anthropic",
    surface: "Messages API",
    retrieval: "Native web search",
    credential: "ANTHROPIC_API_KEY",
  },
  {
    name: "OpenRouter",
    surface: "Online model routing",
    retrieval: "Model-dependent web access",
    credential: "OPENROUTER_API_KEY",
  },
  {
    name: "Google AI Overviews",
    surface: "DataForSEO SERP API",
    retrieval: "Google Organic Live Advanced",
    credential: "DATAFORSEO_API_KEY",
  },
];

const features = [
  [
    "Visibility trends",
    "Measure the share of successful answers that mention your brand over 7, 30, or 90 days.",
  ],
  [
    "Share of voice",
    "Compare your mentions with every competitor you choose to track.",
  ],
  [
    "Citation inventory",
    "See the URLs and domains answer engines use as evidence, including your owned pages.",
  ],
  [
    "Raw answer history",
    "Open every provider response, mention decision, citation, error, and timestamp behind a metric.",
  ],
  [
    "Prompt scheduling",
    "Run the questions that matter on demand or on a recurring worker schedule.",
  ],
  [
    "Provider cost visibility",
    "Show exact upstream request costs when a provider reports them—without invented estimates.",
  ],
  [
    "Bring your own keys",
    "Provider credentials stay server-side and are never sent to the browser or stored in PostgreSQL.",
  ],
  [
    "Self-hosted by default",
    "Run the headless API, worker, and PostgreSQL locally with Docker Compose.",
  ],
];

type PagePath = string;

function normalizePath(pathname: string): PagePath | "not-found" {
  const normalized =
    pathname === "/" ? "/" : `${pathname.replace(/\/+$/, "")}/`;
  if (
    normalized === "/" ||
    normalized === "/seo/" ||
    normalized === "/providers/" ||
    normalized === "/self-hosting/" ||
    normalized === "/docs/getting-started/" ||
    normalized === "/benchmarks/"
  ) {
    return normalized as PagePath;
  }
  const segments = normalized.split("/").filter(Boolean);
  if (segments[0] === "benchmarks") {
    const industry = benchmarkIndustries.find(
      ({ slug }) => slug === segments[1],
    );
    if (industry && segments.length === 2) return normalized;
    if (
      industry &&
      segments.length === 3 &&
      industry.companies.some(
        (label) => benchmarkCompanySlug(label) === segments[2],
      )
    )
      return normalized;
  }
  return "not-found";
}

function Arrow() {
  return (
    <span className="arrow" aria-hidden="true">
      →
    </span>
  );
}

function Logo() {
  return (
    <a className="brand" href="/" aria-label="aeokit home">
      <img
        src="/brand/final-v1/aeokit-logo.svg"
        alt="aeokit"
        width="380"
        height="96"
      />
    </a>
  );
}

function PrimaryNav() {
  return (
    <header className="header-wrap">
      <nav className="nav-shell" aria-label="Main navigation">
        <Logo />
        <div className="nav-center">
          <a href="/#ecosystem">Ecosystem</a>
          <a href={repoUrl}>Runtime</a>
          <a href={agentUrl}>Agent</a>
          <a href={skillsUrl}>Skills</a>
          <a href="/seo/">AEO vs SEO</a>
          <a href="/benchmarks/">Benchmarks</a>
          <a href="/providers/">Providers</a>
          <a href="/docs/getting-started/">Docs</a>
        </div>
        <div className="nav-actions">
          <a
            className="github-link"
            href={repoUrl}
            target="_blank"
            rel="noreferrer"
          >
            GitHub
          </a>
          <a className="nav-button" href={agentUrl}>
            Get aeokit agent
          </a>
          <details className="mobile-menu">
            <summary aria-label="Open navigation menu">Menu</summary>
            <div>
              <a href="/#ecosystem">Ecosystem</a>
              <a href={agentUrl}>aeokit agent</a>
              <a href={skillsUrl}>aeokit skills</a>
              <a href="/seo/">AEO vs SEO</a>
              <a href="/benchmarks/">Benchmarks</a>
              <a href="/providers/">Providers</a>
              <a href="/self-hosting/">Self-hosting</a>
              <a href="/docs/getting-started/">Docs</a>
              <a href={agentUrl}>Get aeokit agent</a>
              <a href={repoUrl} target="_blank" rel="noreferrer">
                GitHub
              </a>
            </div>
          </details>
        </div>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container footer-cta">
        <div>
          <p className="eyebrow">Evidence, not guesswork</p>
          <h2>See what AI answers say about your brand.</h2>
        </div>
        <div className="footer-actions">
          <a className="button primary" href={agentUrl}>
            Get aeokit agent <Arrow />
          </a>
          <a className="button secondary" href={skillsUrl}>
            Get the skills
          </a>
        </div>
      </div>
      <div className="container footer-grid">
        <div className="footer-brand">
          <Logo />
          <p>
            Open infrastructure, agents, and skills for understanding and
            improving how AI answers represent your brand.
          </p>
        </div>
        <div>
          <h3>Ecosystem</h3>
          <a href={repoUrl}>aeokit runtime</a>
          <a href={agentUrl}>aeokit agent</a>
          <a href={skillsUrl}>aeokit skills</a>
          <a href={cloudUrl}>aeokit cloud</a>
          <a href="/providers/">Providers</a>
          <a href="/self-hosting/">Self-hosting</a>
        </div>
        <div>
          <h3>Resources</h3>
          <a href="/docs/getting-started/">Getting started</a>
          <a href="/seo/">AEO vs SEO</a>
          <a href="/benchmarks/">Industry benchmarks</a>
          <a
            href={`${repoUrl}/blob/main/README.md`}
            target="_blank"
            rel="noreferrer"
          >
            Documentation
          </a>
          <a href={`${repoUrl}/issues`} target="_blank" rel="noreferrer">
            Issues
          </a>
        </div>
        <div>
          <h3>Project</h3>
          <a
            href={`${repoUrl}/blob/main/LICENSE`}
            target="_blank"
            rel="noreferrer"
          >
            AGPL-3.0 license
          </a>
          <a
            href={`${repoUrl}/blob/main/SECURITY.md`}
            target="_blank"
            rel="noreferrer"
          >
            Security
          </a>
          <a
            href={`${repoUrl}/blob/main/CONTRIBUTING.md`}
            target="_blank"
            rel="noreferrer"
          >
            Contributing
          </a>
        </div>
      </div>
      <div className="container footer-bottom">
        <span>© {new Date().getFullYear()} aeokit</span>
        <span>Built in the open.</span>
      </div>
    </footer>
  );
}

function PageLayout({ children }: { children: ReactNode }) {
  return (
    <div className="site">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <PrimaryNav />
      <main id="main-content">{children}</main>
      <Footer />
    </div>
  );
}

function CodeBlock({ children, label }: { children: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_500);
  }
  return (
    <div className="code-block">
      <div className="code-bar">
        <span>{label ?? "Terminal"}</span>
        <button type="button" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre>
        <code>{children}</code>
      </pre>
    </div>
  );
}

const showcaseViews = [
  {
    id: "visibility",
    label: "Visibility",
    caption:
      "How often answer engines mention your brand versus cite one of your domains.",
  },
  {
    id: "share-of-voice",
    label: "Share of Voice",
    caption: "Your brand's share of all tracked brand and competitor mentions.",
  },
  {
    id: "citations",
    label: "Citations",
    caption:
      "The pages and domains answer engines use as evidence in tracked answers.",
  },
] as const;

type ShowcaseViewId = (typeof showcaseViews)[number]["id"];

const demoNavGroups: Array<{
  label: string;
  items: Array<{ id: string; label: string; icon: LucideIcon }>;
}> = [
  {
    label: "Overview",
    items: [{ id: "dashboard", label: "Dashboard", icon: Gauge }],
  },
  {
    label: "Tracking",
    items: [
      { id: "visibility", label: "Visibility", icon: ChartNoAxesCombined },
      { id: "prompts", label: "Prompts", icon: FileQuestion },
      {
        id: "share-of-voice",
        label: "Share of Voice",
        icon: BarChart3,
      },
      { id: "citations", label: "Citations", icon: Link2 },
      { id: "ai-referrals", label: "AI Referrals", icon: MousePointerClick },
      { id: "runs", label: "Run History", icon: Clock3 },
    ],
  },
  {
    label: "Configure",
    items: [
      { id: "competitors", label: "Competitors", icon: Swords },
      { id: "settings", label: "Settings", icon: Settings },
    ],
  },
];

const demoVisibilityRows = [
  {
    prompt: "What are the best open-source AI visibility tracking tools?",
    mentionRate: "75%",
    citationRate: "50%",
    evidence: "21 mentioned · 14 cited / 28",
    providers: ["OpenAI", "Anthropic"],
    lastRun: "2 minutes ago",
  },
  {
    prompt: "Which tools help brands improve visibility in AI answers?",
    mentionRate: "75%",
    citationRate: "50%",
    evidence: "21 mentioned · 14 cited / 28",
    providers: ["OpenAI", "Anthropic"],
    lastRun: "8 minutes ago",
  },
  {
    prompt: "Compare self-hosted AEO platforms for a small marketing team.",
    mentionRate: "75%",
    citationRate: "50%",
    evidence: "21 mentioned · 14 cited / 28",
    providers: ["OpenAI", "Anthropic"],
    lastRun: "14 minutes ago",
  },
];

const demoShareRows = [
  { name: "aeokit", mentions: 63, share: 59 },
  { name: "Acme Analytics", mentions: 28, share: 26 },
  { name: "Northstar", mentions: 16, share: 15 },
];

const demoCitationRows = [
  {
    title: "aeokit documentation",
    url: "https://aeokit.dev/docs/getting-started/",
    category: "owned",
    provider: "OpenAI",
    prompt: "What are the best open-source AI visibility tracking tools?",
    found: "2 minutes ago",
  },
  {
    title: "SEO community discussion",
    url: "https://www.reddit.com/r/seo",
    category: "social",
    provider: "OpenAI",
    prompt: "What are the best open-source AI visibility tracking tools?",
    found: "2 minutes ago",
  },
  {
    title: "aeokit documentation",
    url: "https://aeokit.dev/docs/getting-started/",
    category: "owned",
    provider: "Anthropic",
    prompt: "Which tools help brands improve visibility in AI answers?",
    found: "8 minutes ago",
  },
];

function DemoSidebar({ activeView }: { activeView: ShowcaseViewId }) {
  return (
    <aside className="demo-app-sidebar">
      <div className="demo-app-brand">
        <span>A</span>
        <b>aeokit</b>
      </div>
      <div className="demo-project-select">
        <span>aeokit</span>
        <i aria-hidden="true" />
      </div>
      <nav aria-label="aeokit application preview">
        {demoNavGroups.map((group) => (
          <div className="demo-nav-group" key={group.label}>
            <b>{group.label}</b>
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = item.id === activeView;
              return (
                <div className={active ? "active" : ""} key={item.id}>
                  <Icon aria-hidden="true" />
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="demo-sidebar-footer">
        <span>
          <BookOpenText aria-hidden="true" /> Documentation
          <ExternalLink aria-hidden="true" />
        </span>
        <span>
          <CircleHelp aria-hidden="true" /> Help &amp; Community
        </span>
        <span className="muted">
          <Bot aria-hidden="true" /> Self-hosted
        </span>
      </div>
    </aside>
  );
}

function DemoPageHeader({
  title,
  description,
  period = false,
}: {
  title: string;
  description: string;
  period?: boolean;
}) {
  return (
    <div className="demo-page-header">
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      {period ? <span className="demo-period-select">Last 30 days</span> : null}
    </div>
  );
}

function DemoMetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="demo-metric-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function DemoProviderBadge({ children }: { children: ReactNode }) {
  return <span className="demo-provider-badge">{children}</span>;
}

function DemoVisibilityPage() {
  return (
    <div className="demo-page">
      <DemoPageHeader
        title="Visibility"
        description="How often answer engines mention your brand versus cite one of your domains."
        period
      />
      <div className="demo-metrics demo-metrics-four">
        <DemoMetricCard label="Mention rate" value="75%" />
        <DemoMetricCard label="Citation rate" value="50%" />
        <DemoMetricCard label="Brand mentions" value="63" />
        <DemoMetricCard label="Successful runs" value="84" />
      </div>
      <section className="demo-data-panel demo-visibility-table">
        <table className="demo-table">
          <thead>
            <tr>
              <th>Prompt</th>
              <th>Mention rate</th>
              <th>Citation rate</th>
              <th>Evidence</th>
              <th>Providers</th>
              <th>Last run</th>
            </tr>
          </thead>
          <tbody>
            {demoVisibilityRows.map((row) => (
              <tr key={row.prompt}>
                <td className="demo-prompt-cell">{row.prompt}</td>
                <td>
                  <span className="demo-rate">
                    <CircleCheck aria-hidden="true" /> {row.mentionRate}
                  </span>
                </td>
                <td>
                  <span className="demo-rate">
                    <CircleCheck aria-hidden="true" /> {row.citationRate}
                  </span>
                </td>
                <td className="demo-nowrap muted">{row.evidence}</td>
                <td>
                  <div className="demo-provider-list">
                    {row.providers.map((provider) => (
                      <DemoProviderBadge key={provider}>
                        {provider}
                      </DemoProviderBadge>
                    ))}
                  </div>
                </td>
                <td className="demo-nowrap muted">{row.lastRun}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function DemoShareOfVoicePage() {
  return (
    <div className="demo-page">
      <DemoPageHeader
        title="Share of Voice"
        description="Your brand's share of all tracked brand and competitor mentions."
        period
      />
      <div className="demo-sov-grid">
        <section className="demo-data-panel demo-landscape-panel">
          <div className="demo-landscape-heading">
            <span>
              <Trophy aria-hidden="true" />
            </span>
            <div>
              <b>Competitive landscape</b>
              <small>107 total mentions</small>
            </div>
          </div>
          <div className="demo-share-bars">
            {demoShareRows.map((row) => (
              <div key={row.name}>
                <p>
                  <span>
                    {row.name}
                    {row.name === "aeokit" ? <em>You</em> : null}
                  </span>
                  <b>{row.share}%</b>
                </p>
                <div>
                  <i
                    className={row.name === "aeokit" ? "owned" : ""}
                    style={{ width: `${row.share}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="demo-data-panel demo-leaderboard">
          <h4>Leaderboard</h4>
          <table className="demo-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Brand</th>
                <th>Mentions</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {demoShareRows.map((row, index) => (
                <tr key={row.name}>
                  <td className="muted">{index + 1}</td>
                  <td className="demo-prompt-cell">{row.name}</td>
                  <td>{row.mentions}</td>
                  <td className="demo-mono-strong">{row.share}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

function DemoCitationsPage() {
  return (
    <div className="demo-page">
      <DemoPageHeader
        title="Citations"
        description="The pages and domains answer engines use as evidence in tracked answers."
      />
      <div className="demo-metrics demo-metrics-three">
        <DemoMetricCard label="Total citations" value="84" />
        <DemoMetricCard label="Unique domains" value="2" />
        <DemoMetricCard label="Owned citations" value="42" />
      </div>
      <div className="demo-filters">
        <span className="demo-search-field">
          <Search aria-hidden="true" /> Search URLs and domains
        </span>
        <span className="demo-period-select">All categories</span>
      </div>
      <section className="demo-data-panel demo-citations-table">
        <table className="demo-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Category</th>
              <th>Provider</th>
              <th>Prompt</th>
              <th>Found</th>
            </tr>
          </thead>
          <tbody>
            {demoCitationRows.map((row, index) => (
              <tr key={`${row.url}-${row.provider}-${index}`}>
                <td>
                  <span className="demo-source-title">
                    {row.title} <ExternalLink aria-hidden="true" />
                  </span>
                  <small>{row.url}</small>
                </td>
                <td>
                  <span className={`demo-category ${row.category}`}>
                    {row.category}
                  </span>
                </td>
                <td>
                  <DemoProviderBadge>{row.provider}</DemoProviderBadge>
                </td>
                <td className="demo-citation-prompt muted">{row.prompt}</td>
                <td className="demo-nowrap muted">{row.found}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function DemoAppPage({ activeView }: { activeView: ShowcaseViewId }) {
  if (activeView === "share-of-voice") return <DemoShareOfVoicePage />;
  if (activeView === "citations") return <DemoCitationsPage />;
  return <DemoVisibilityPage />;
}

function VisibilityPreview() {
  const [activeView, setActiveView] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const isPaused = userPaused || isInteracting;
  const view = showcaseViews[activeView];

  useEffect(() => {
    if (
      isPaused ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveView((current) => (current + 1) % showcaseViews.length);
    }, 4_800);

    return () => window.clearInterval(timer);
  }, [isPaused]);

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsInteracting(false);
    }
  }

  function handleTabKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    const lastIndex = showcaseViews.length - 1;
    let nextIndex: number | undefined;

    if (event.key === "ArrowRight")
      nextIndex = index === lastIndex ? 0 : index + 1;
    if (event.key === "ArrowLeft")
      nextIndex = index === 0 ? lastIndex : index - 1;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = lastIndex;
    if (nextIndex === undefined) return;

    event.preventDefault();
    setActiveView(nextIndex);
    window.requestAnimationFrame(() => {
      document
        .getElementById(`showcase-tab-${showcaseViews[nextIndex].id}`)
        ?.focus();
    });
  }

  return (
    <div
      className={`product-showcase showcase-${view.id}`}
      data-paused={isPaused}
      onMouseEnter={() => setIsInteracting(true)}
      onMouseLeave={() => setIsInteracting(false)}
      onFocus={() => setIsInteracting(true)}
      onBlur={handleBlur}
    >
      <div className="showcase-controls">
        <div
          className="showcase-tabs"
          role="tablist"
          aria-label="Product views"
        >
          {showcaseViews.map((item, index) => (
            <button
              id={`showcase-tab-${item.id}`}
              key={item.id}
              type="button"
              role="tab"
              aria-selected={activeView === index}
              aria-controls="showcase-panel"
              tabIndex={activeView === index ? 0 : -1}
              onClick={() => setActiveView(index)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
            >
              <span aria-hidden="true" />
              {item.label}
            </button>
          ))}
        </div>
        <button
          className="showcase-toggle"
          type="button"
          aria-label={
            userPaused ? "Play product showcase" : "Pause product showcase"
          }
          onClick={() => setUserPaused((paused) => !paused)}
        >
          <span aria-hidden="true">{userPaused ? "▶" : "Ⅱ"}</span>
          {userPaused ? "Play" : "Pause"}
        </button>
      </div>

      <div className="showcase-stage">
        <div
          className="product-preview"
          id="showcase-panel"
          key={view.id}
          role="tabpanel"
          aria-labelledby={`showcase-tab-${view.id}`}
        >
          <span className="demo-cursor" aria-hidden="true">
            <i />
          </span>
          <div className="demo-app-shell">
            <DemoSidebar activeView={view.id} />
            <div className="demo-app-content">
              <div className="demo-mobile-bar">
                <Menu aria-hidden="true" />
                <b>aeokit</b>
              </div>
              <main className="demo-app-window">
                <DemoAppPage activeView={view.id} />
              </main>
            </div>
          </div>
        </div>

        <div className="showcase-caption" key={`${view.id}-caption`}>
          <span className="showcase-caption-icon" aria-hidden="true" />
          <div>
            <b>{view.label}</b>
            <span>{view.caption}</span>
          </div>
          <a href="/docs/getting-started/">
            Explore <Arrow />
          </a>
        </div>
      </div>
    </div>
  );
}

function HomePage() {
  return (
    <>
      <section className="hero">
        <div className="container hero-grid">
          <div>
            <p className="hero-kicker">
              <span /> Runtime · Agent · Skills
            </p>
            <h1>Know why AI answers mention—or miss—your brand.</h1>
            <p className="hero-copy">
              aeokit helps you observe what answer engines say, investigate the
              evidence behind it, and make verifiable improvements—from first
              prompt to final patch.
            </p>
            <div className="hero-actions">
              <a className="button primary large" href={agentUrl}>
                Get aeokit agent <Arrow />
              </a>
              <a className="button secondary large" href={repoUrl}>
                View on GitHub
              </a>
            </div>
            <p className="hero-proof">
              Open source · Local-first · Every metric has receipts
            </p>
          </div>
          <div className="hero-code">
            <p>Give your agent an AEO workflow</p>
            <CodeBlock label="Terminal">
              {
                "npx aeo-skills@latest add aeo-audit --to agents\n\n# Then ask your agent:\n$aeo-audit Audit this site for a buyer question"
              }
            </CodeBlock>
            <a href={skillsUrl}>
              Explore aeokit skills <Arrow />
            </a>
          </div>
        </div>
      </section>

      <section className="section ecosystem-section" id="ecosystem">
        <div className="container">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow">The aeokit ecosystem</p>
              <h2>Use one layer. Or connect all three.</h2>
            </div>
            <p>
              Start with a portable workflow, run a focused local agent, or
              build directly on the headless runtime. Every layer favors
              inspectable evidence over opaque recommendations.
            </p>
          </div>
          <div className="ecosystem-grid">
            <article>
              <span className="ecosystem-number">01 · Runtime</span>
              <h3>aeokit runtime</h3>
              <p>
                The headless API and worker for prompts, answers, citations,
                mentions, competitors, visibility metrics, and opportunities.
              </p>
              <div className="ecosystem-tags">
                <span>JSON API</span>
                <span>CLI + MCP</span>
                <span>PostgreSQL</span>
              </div>
              <a className="text-link" href={repoUrl}>
                Explore the runtime <Arrow />
              </a>
            </article>
            <article className="featured">
              <span className="ecosystem-number">02 · Agent</span>
              <h3>aeokit agent</h3>
              <p>
                A local-first agent that plans, investigates, and improves
                answer-engine visibility while keeping evidence and changes in
                view.
              </p>
              <div className="ecosystem-tags">
                <span>Local-first</span>
                <span>Evidence-aware</span>
                <span>Actionable</span>
              </div>
              <a className="text-link" href={agentUrl}>
                Meet the agent <Arrow />
              </a>
            </article>
            <article>
              <span className="ecosystem-number">03 · Skills</span>
              <h3>aeokit skills</h3>
              <p>
                Portable audit, improve, and observe workflows for Codex, Claude
                Code, ChatGPT, Cursor, Copilot, Gemini CLI, and aeokit agent.
              </p>
              <div className="ecosystem-tags">
                <span>Audit</span>
                <span>Improve</span>
                <span>Observe</span>
              </div>
              <a className="text-link" href={skillsUrl}>
                Browse the skills <Arrow />
              </a>
            </article>
          </div>
        </div>
      </section>

      <section className="provider-strip" aria-label="Supported providers">
        <div className="container">
          <span>SUPPORTED PROVIDERS</span>
          {providers.map((provider) => (
            <b key={provider.name}>{provider.name}</b>
          ))}
        </div>
      </section>

      <section className="section product-section" id="product">
        <div className="container">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow">Evidence in practice</p>
              <h2>Every metric has receipts.</h2>
            </div>
            <p>
              The interactive example below shows the evidence model in action.
              Visibility percentages are useful only when you can inspect the
              prompt, provider, answer, mention decision, and citation behind
              them. aeokit keeps the evidence attached and makes it available to
              apps, agents, CLI tools, and integrations.
            </p>
          </div>
          <VisibilityPreview />
        </div>
      </section>

      <section className="section how-section">
        <div className="container">
          <div className="section-heading">
            <p className="eyebrow">How it works</p>
            <h2>From prompts to proof in three steps.</h2>
          </div>
          <div className="step-grid">
            {[
              [
                "01",
                "Define the market",
                "Add your brand, aliases, owned domains, and the competitors that matter.",
              ],
              [
                "02",
                "Run real questions",
                "Choose providers, schedule prompts, and preserve each grounded answer exactly as returned.",
              ],
              [
                "03",
                "Measure and inspect",
                "Track visibility, share of voice, citations, and costs—then open the underlying run.",
              ],
            ].map(([number, title, text]) => (
              <article key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section features-section">
        <div className="container">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow">Shipped today</p>
              <h2>Visibility tracking without the black box.</h2>
            </div>
            <p>
              The runtime stores the observations. The agent investigates and
              acts. The skills make repeatable AEO practice portable across the
              tools your team already uses.
            </p>
          </div>
          <div className="feature-grid">
            {features.map(([title, text], index) => (
              <article key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section honesty-section">
        <div className="container honesty-grid">
          <div>
            <p className="eyebrow light">Coverage</p>
            <h2>Provider coverage, stated precisely.</h2>
            <p>
              aeokit uses documented APIs and web-search capabilities. It does
              not claim that an API response is identical to a consumer chat
              interface.
            </p>
            <a className="button light-button" href="/providers/">
              See provider details <Arrow />
            </a>
          </div>
          <div className="coverage-list">
            {providers.map((provider) => (
              <div key={provider.name}>
                <span>{provider.name}</span>
                <b>{provider.surface}</b>
                <small>{provider.retrieval}</small>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section open-section">
        <div className="container open-grid">
          <div>
            <p className="eyebrow">Why open source</p>
            <h2>
              Your visibility data should not be trapped in another dashboard.
            </h2>
          </div>
          <div>
            <p>
              Inspect the scoring logic, store raw answers in your database,
              choose your providers, and customize the workflow. The runtime is
              AGPL-3.0 licensed and designed to run on infrastructure you
              control.
            </p>
            <div className="open-actions">
              <a className="button primary" href="/self-hosting/">
                Explore self-hosting <Arrow />
              </a>
              <a
                className="text-link"
                href={repoUrl}
                target="_blank"
                rel="noreferrer"
              >
                Browse the source <Arrow />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function InteriorHero({
  eyebrow,
  title,
  text,
}: {
  eyebrow: string;
  title: string;
  text: string;
}) {
  return (
    <section className="interior-hero">
      <div className="container narrow">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
    </section>
  );
}

function ProvidersPage() {
  return (
    <>
      <InteriorHero
        eyebrow="Provider coverage"
        title="Coverage without hand-waving."
        text="aeokit tells you which API produced an answer, how retrieval was enabled, which model ran, and which citations came back."
      />
      <section className="section detail-section">
        <div className="container">
          <div
            className="provider-table"
            role="table"
            aria-label="Supported provider details"
          >
            <div className="provider-row provider-head" role="row">
              <span role="columnheader">Provider</span>
              <span role="columnheader">Surface</span>
              <span role="columnheader">Retrieval</span>
              <span role="columnheader">Credential</span>
            </div>
            {providers.map((provider) => (
              <div className="provider-row" role="row" key={provider.name}>
                <b role="cell">{provider.name}</b>
                <span role="cell">{provider.surface}</span>
                <span role="cell">{provider.retrieval}</span>
                <code role="cell">{provider.credential}</code>
              </div>
            ))}
          </div>
          <div className="disclosure-card">
            <span>Important distinction</span>
            <h2>API responses are not consumer-interface captures.</h2>
            <p>
              OpenAI and Anthropic use native web-search tools. OpenRouter uses
              online model routing. Google AI Overviews come from DataForSEO’s
              Google Organic Live Advanced endpoint. aeokit reports those
              surfaces as they are instead of presenting them as
              browser-equivalent ChatGPT, Claude, or Google sessions.
            </p>
          </div>
          <div className="detail-grid">
            <article>
              <span>01</span>
              <h3>Choose per prompt</h3>
              <p>
                Each prompt records its provider, target model, web-search
                setting, cadence, and active state.
              </p>
            </article>
            <article>
              <span>02</span>
              <h3>Keep exact costs</h3>
              <p>
                When OpenRouter or DataForSEO reports a dollar amount, aeokit
                stores and displays it. Missing costs remain blank rather than
                estimated.
              </p>
            </article>
            <article>
              <span>03</span>
              <h3>Preserve the response</h3>
              <p>
                Answers, citations, web queries, latency, model identifiers,
                errors, and raw provider payloads remain connected to the run.
              </p>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}

function SelfHostingPage() {
  return (
    <>
      <InteriorHero
        eyebrow="Self-hosting"
        title="Your prompts, keys, answers, and costs—on infrastructure you control."
        text="aeokit ships as a portable Docker Compose stack: a headless Hono API, PostgreSQL, migrations, and a pg-boss worker."
      />
      <section className="section detail-section">
        <div className="container hosting-grid">
          <div>
            <p className="eyebrow">One command</p>
            <h2>Run the headless runtime locally.</h2>
            <p className="body-copy">
              Add at least one provider key to <code>.env</code>, then start the
              database, API, migrations, and background worker together.
            </p>
          </div>
          <CodeBlock label="Docker Compose">{`${quickStart}\ncp .env.example .env\n# Add provider keys only when running live requests\ndocker compose -p openaeo up -d --build app worker`}</CodeBlock>
        </div>
        <div className="container data-flow">
          {[
            ["Clients", "Apps, agents, CLI, and MCP", "JSON + OpenAPI"],
            [
              "API + worker",
              "Provider calls, analysis, and schedules",
              "Keys read from environment",
            ],
            [
              "PostgreSQL",
              "Projects, prompts, answers, citations",
              "Your database",
            ],
          ].map(([label, purpose, detail], index) => (
            <article key={label}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{label}</h3>
              <p>{purpose}</p>
              <small>{detail}</small>
            </article>
          ))}
        </div>
        <div className="container detail-grid security-grid">
          <article>
            <span>Keys</span>
            <h3>Server-side only</h3>
            <p>
              Provider credentials are never sent to the browser or persisted in
              PostgreSQL.
            </p>
          </article>
          <article>
            <span>Storage</span>
            <h3>PostgreSQL-backed</h3>
            <p>
              Raw answers and derived metrics remain queryable in the database
              you operate.
            </p>
          </article>
          <article>
            <span>License</span>
            <h3>AGPL-3.0 licensed</h3>
            <p>
              Inspect, customize, and deploy the application without a
              proprietary runtime dependency, with network use covered by the
              open-source license.
            </p>
          </article>
        </div>
      </section>
    </>
  );
}

function GettingStartedPage() {
  return (
    <>
      <InteriorHero
        eyebrow="Documentation"
        title="Go from clone to your first tracked answer."
        text="Use Docker Compose for the shortest path. You need Docker and one supported provider credential."
      />
      <section className="section docs-section">
        <div className="container docs-layout">
          <aside aria-label="On this page">
            <span>ON THIS PAGE</span>
            <a href="#clone">Clone the repository</a>
            <a href="#configure">Configure a provider</a>
            <a href="#start">Start aeokit</a>
            <a href="#first-run">Create your first run</a>
          </aside>
          <div className="docs-content">
            <section id="clone">
              <span className="step-number">01</span>
              <h2>Clone the repository</h2>
              <p>aeokit requires Docker for the default local setup.</p>
              <CodeBlock>{quickStart}</CodeBlock>
            </section>
            <section id="configure">
              <span className="step-number">02</span>
              <h2>Configure at least one provider</h2>
              <p>
                Copy the example environment file, then add one or more
                supported credentials. Keys remain on the server.
              </p>
              <CodeBlock>{`cp .env.example .env\n\n# Choose any combination:\nOPENAI_API_KEY=\nANTHROPIC_API_KEY=\nOPENROUTER_API_KEY=\nDATAFORSEO_API_KEY=`}</CodeBlock>
              <p className="docs-note">
                DataForSEO expects a Base64-encoded{" "}
                <code>login:API-password</code> value.
              </p>
            </section>
            <section id="start">
              <span className="step-number">03</span>
              <h2>Start the stack</h2>
              <CodeBlock>
                {"docker compose -p openaeo up -d --build app worker"}
              </CodeBlock>
              <p>
                Open <code>http://127.0.0.1:3000/docs</code> for interactive API
                documentation, or connect with the aeokit CLI.
              </p>
            </section>
            <section id="first-run">
              <span className="step-number">04</span>
              <h2>Track your first answer</h2>
              <ol>
                <li>Create a project through the API, CLI, or MCP server.</li>
                <li>Add a buyer question and the competitors that matter.</li>
                <li>Select a configured provider and run an analysis.</li>
                <li>
                  Inspect the answer, mention decision, citations, latency, and
                  exact cost when available.
                </li>
              </ol>
              <div className="docs-actions">
                <a
                  className="button primary"
                  href={`${repoUrl}/blob/main/README.md`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Read the full README <Arrow />
                </a>
                <a
                  className="text-link"
                  href={`${repoUrl}/issues`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Get help on GitHub <Arrow />
                </a>
              </div>
            </section>
          </div>
        </div>
      </section>
    </>
  );
}

function NotFoundPage() {
  return (
    <section className="not-found">
      <div className="container narrow">
        <p className="eyebrow">404</p>
        <h1>That page is not part of aeokit.</h1>
        <p>
          Return to the product overview or use the getting-started guide to run
          aeokit locally.
        </p>
        <div className="hero-actions">
          <a className="button primary" href="/">
            Back to aeokit
          </a>
          <a className="button secondary" href="/docs/getting-started/">
            Getting started
          </a>
        </div>
      </div>
    </section>
  );
}

export default function App({ pathname }: { pathname?: string }) {
  const path = normalizePath(
    pathname ??
      (typeof window === "undefined" ? "/" : window.location.pathname),
  );
  let page: ReactNode;
  if (path === "/") page = <HomePage />;
  else if (path === "/seo/") page = <SeoPage />;
  else if (path === "/providers/") page = <ProvidersPage />;
  else if (path === "/self-hosting/") page = <SelfHostingPage />;
  else if (path === "/docs/getting-started/") page = <GettingStartedPage />;
  else if (path === "/benchmarks/") page = <BenchmarksPage />;
  else if (path.startsWith("/benchmarks/")) {
    const segments = path.split("/").filter(Boolean);
    const industrySlug = segments[1] ?? "";
    const companySlug = segments[2];
    page = companySlug ? (
      <CompanyBenchmarkPage
        industrySlug={industrySlug}
        companySlug={companySlug}
      />
    ) : (
      <IndustryBenchmarkPage industrySlug={industrySlug} />
    );
  } else page = <NotFoundPage />;
  return <PageLayout>{page}</PageLayout>;
}
